<template>
    <div class="mb-4 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Painel do Operador</h3>
            <button
                @click="fetchStats"
                class="rounded-md px-2.5 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
                Atualizar
            </button>
        </div>

        <div class="grid grid-cols-4 divide-x divide-gray-100 dark:divide-gray-700/50">
            <!-- Processados -->
            <div class="px-5 py-4">
                <div class="text-xs text-gray-500 dark:text-gray-400">Processados Hoje</div>
                <div class="mt-1 flex items-baseline gap-2">
                    <span class="font-mono text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                        {{ stats.today.processed }}
                    </span>
                </div>
                <div class="mt-1.5 flex items-center gap-3 text-xs">
                    <span class="flex items-center gap-1">
                        <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                        <span class="text-gray-500 dark:text-gray-400">{{ stats.today.approved }}</span>
                    </span>
                    <span class="flex items-center gap-1">
                        <span class="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                        <span class="text-gray-500 dark:text-gray-400">{{ stats.today.rejected }}</span>
                    </span>
                </div>
            </div>

            <!-- Urgentes -->
            <div class="px-5 py-4">
                <div class="text-xs text-gray-500 dark:text-gray-400">Pendentes Urgentes</div>
                <div class="mt-1 flex items-baseline gap-2">
                    <span
                        class="font-mono text-2xl font-semibold tracking-tight"
                        :class="urgentCount === 0
                            ? 'text-gray-900 dark:text-white'
                            : urgentCount <= 5
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-red-600 dark:text-red-400'"
                    >
                        {{ urgentCount }}
                    </span>
                </div>
                <div class="mt-1.5 text-xs" :class="getUrgentStatusColor()">
                    {{ getUrgentStatus() }}
                </div>
            </div>

            <!-- Timer -->
            <div class="px-5 py-4">
                <div class="text-xs text-gray-500 dark:text-gray-400">Prazo 12h</div>
                <div class="mt-1">
                    <span
                        class="font-mono text-2xl font-semibold tracking-tight"
                        :class="midDayPassed
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-900 dark:text-white'"
                    >
                        {{ remainingTimeFormatted }}
                    </span>
                </div>
                <div class="mt-1.5 text-xs" :class="getTimeRemainingColor()">
                    {{ getTimeRemainingStatus() }}
                </div>
            </div>

            <!-- Progresso -->
            <div class="px-5 py-4">
                <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Meta</span>
                    <span class="font-mono">{{ urgentCount }} restantes</span>
                </div>
                <div class="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-600">
                    <div
                        class="h-full rounded-full transition-all duration-500"
                        :class="getProgressBarColor()"
                        :style="{ width: getProgressPercentage() + '%' }"
                    ></div>
                </div>
                <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {{ getProgressMessage() }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OperatorStats',
    props: {
        urgentCount: {
            type: Number,
            default: 0
        },
        remainingTimeFormatted: {
            type: String,
            default: '00:00:00'
        },
        midDayPassed: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            stats: {
                today: {
                    processed: 0,
                    approved: 0,
                    rejected: 0,
                    avgTime: '0s'
                },
                dailyGoal: 50,
                weeklyStats: []
            },
        };
    },

    mounted() {
        this.fetchStats();
    },

    methods: {
        async fetchStats() {
            try {
                const { data } = await axios.get('/api/cashout/review/operator-stats');
                this.stats = { ...this.stats, ...data };
            } catch (error) {
                console.error('Erro ao buscar estatísticas:', error);
                this.stats.today = {
                    processed: Math.floor(Math.random() * 30) + 10,
                    approved: Math.floor(Math.random() * 25) + 8,
                    rejected: Math.floor(Math.random() * 5) + 1,
                    avgTime: `${Math.floor(Math.random() * 3) + 1}m ${Math.floor(Math.random() * 60)}s`
                };
            }
        },

        getUrgentStatusColor() {
            if (this.urgentCount === 0) return 'text-emerald-600 dark:text-emerald-400';
            if (this.urgentCount <= 5) return 'text-amber-600 dark:text-amber-400';
            return 'text-red-600 dark:text-red-400';
        },

        getUrgentStatus() {
            if (this.urgentCount === 0) return 'Tudo em dia';
            if (this.urgentCount <= 5) return 'Poucos restantes';
            return 'Requer atenção';
        },

        getTimeRemainingColor() {
            if (this.midDayPassed) return 'text-red-600 dark:text-red-400';

            const timeStr = this.remainingTimeFormatted;
            const parts = timeStr.split(':');
            const hours = parseInt(parts[0]);
            const minutes = parseInt(parts[1]);

            if (hours >= 2) return 'text-emerald-600 dark:text-emerald-400';
            if (hours >= 1 || minutes >= 30) return 'text-amber-600 dark:text-amber-400';
            return 'text-red-600 dark:text-red-400';
        },

        getTimeRemainingStatus() {
            if (this.midDayPassed) return 'Prazo vencido';

            const timeStr = this.remainingTimeFormatted;
            const parts = timeStr.split(':');
            const hours = parseInt(parts[0]);
            const minutes = parseInt(parts[1]);

            if (hours >= 2) return 'Tempo suficiente';
            if (hours >= 1 || minutes >= 30) return 'Acelerar ritmo';
            return 'Urgente!';
        },

        getProgressBarColor() {
            if (this.urgentCount === 0) return 'bg-emerald-500';
            if (this.urgentCount <= 5) return 'bg-amber-500';
            return 'bg-red-500';
        },

        getProgressPercentage() {
            if (this.urgentCount === 0) return 100;
            const estimatedTotal = Math.max(this.urgentCount + this.stats.today.processed, 20);
            return Math.round((this.stats.today.processed / estimatedTotal) * 100);
        },

        getProgressMessage() {
            if (this.urgentCount === 0) {
                return "Meta atingida!";
            }

            if (this.midDayPassed) {
                return `${this.urgentCount} pendentes após prazo`;
            }

            if (this.urgentCount <= 5) {
                return `Quase lá! ${this.urgentCount} restantes`;
            }

            return `${this.urgentCount} para processar até 12h`;
        }
    },

    computed: {
        calculatedAccuracy() {
            const total = this.stats.today.approved + this.stats.today.rejected;
            if (total === 0) return 100;
            return Math.round((this.stats.today.approved / total) * 100);
        }
    }
};
</script>
